<template>
    <v-sheet color="white"
             elevation="5"
             rounded
             class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3">
        <v-dialog v-model="showValidationDialog">
            <v-card class="search-modal">
                <h2>Validation Results</h2>
                <v-form class="search-form">
                    <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">
                        <pre>{{ validationResult.errors.join("\r\n") }}</pre>
                    </v-alert>
                    <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined>
                        <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
                    </v-alert>
                    <v-btn large
                           color="primary"
                           width="125px"
                           :disabled="!validationResult.canBeSaved"
                           v-on:click="saveChanges">
                        <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
                        Save
                    </v-btn>
                </v-form>
            </v-card>
        </v-dialog>
        <div class="profile-content">
            <v-row>
                <h1 v-if="!addNewMode">
                    Lease ID #{{ this.leaseId }}
                </h1>
                <h1 class="mb-3" v-else>Create New Lease</h1>
                <v-spacer />

            </v-row>
            <v-row>
                <v-textarea :disabled="!isEditMode"
                            :visible="!isEditMode"
                            outlined
                            auto-grow
                            clearable
                            rows="1"
                            v-model="leaseInfo.adminNotes"
                            label="Description of changes (required)" />
                <v-btn class="ml-1 mr-1"
                       :disabled="isEditMode && !leaseInfo.adminNotes"
                       color="primary"
                       @click="toggleEditMode">
                    {{ isEditMode ? "Save" : "Edit" }}
                </v-btn>
            </v-row>

            <v-row class="mt-5">
                <v-col cols="4" class="text-left">  
                    <AdminDogSearchDialog 
                        label="Leased Aussie"
                        v-bind:dogId.sync="leaseInfo.dogId"
                        :initialDogId="persistedDogId" 
                        :showOwners="true"
                        :isEditMode="isEditMode" /> 
                </v-col>
                <v-col cols="3">
                    <DatepickerFormatted :disabled="!isEditMode"
                                :formattedDate="leaseInfo.startDateFormatted"
                                :date="leaseInfo.startDate"
                                label="Start Date*"
                                v-on:updateDates="updateDates($event, 'startDate')" />
                    <DatepickerFormatted :disabled="!isEditMode"
                                :formattedDate="leaseInfo.endDateFormatted"
                                :date="leaseInfo.endDate"
                                label="End Date*"
                                v-on:updateDates="updateDates($event, 'endDate')" />
                    <DatepickerFormatted :disabled="!isEditMode"
                                :formattedDate="leaseInfo.registrationDateFormatted"
                                :date="leaseInfo.registrationDate"
                                label="Registration Date"
                                v-on:updateDates="updateDates($event, 'registrationDate')" />
                    <span v-if="!addNewMode">
                        <v-checkbox v-model="isCancelled"
                                    :disabled="!isEditMode"
                                    label="Cancelled"
                                    :value="isCancelled"></v-checkbox>
                    </span>
                </v-col>


                <v-col class="ml-5">
                    <AdminEditableCoownersList :isEditMode="isEditMode"
                                               :owners="leaseInfo.leaseOwners"
                                               :ownershipType="leaseOwnershipType"
                                               v-on:updateSelectedPeople="updateSelectedPeople($event)" />

                </v-col>
            </v-row>

            <v-row>
                <b>Notes: </b>
            </v-row>
            <v-row>
                <v-textarea disabled
                            @input="updateText('notes', $event)"
                            :value="leaseInfo.notes"
                            outlined
                            rows="1"
                            auto-grow></v-textarea>
            </v-row>
            <v-snackbar v-model="showInfoMessage" title color="green">
                {{ infoMessage }}
                <template v-slot:action="{ attrs }">
                <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
                    Close
                </v-btn>
                </template>
            </v-snackbar>
            <v-snackbar v-model="showErrorMessage" title color="red">
                {{ errorMessage }}
                <template v-slot:action="{ attrs }">
                <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
                    Close
                </v-btn>
                </template>
            </v-snackbar>
        </div>
    </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";
import AdminDogSearchDialog from "@/components/Admin/AdminDogSearchDialog.vue";
import AdminEditableCoownersList, { OwnershipType } from "@/components/Admin/AdminEditableCoownersList.vue";

import leaseService from "@/services/admin/leaseService";  

import { UserInfo } from "@/types/User";
import { Messages } from "@/constants";

export default Vue.extend({
    name: "LeaseDetailsForAdmin",
    components: {
        DatepickerFormatted,
        AdminEditableCoownersList,
        AdminDogSearchDialog
    },
    data: function () {
        return {
            leaseInfo: {
                dogId: null as any
            } as any,
            isEditMode: false,
            addNewMode: false,        
            persistedDogId: null as any,
            showValidationDialog: false,
            validationResult: {} as any,
            isCancelled: false,
            errorMessage: '',
            infoMessage: '',
            showErrorMessage: false,
            showInfoMessage: false
        };
    },
    methods: {
        updateText: function (key: string, event: string) {
            this.$data.leaseInfo[key] = event;
        },
        reloadData: function () {
            if (this.leaseId) {
                this.getLeaseInfo();
                this.addNewMode = false;
                this.isEditMode = false;
            }
            else {
                this.addNewMode = true;
                this.isEditMode = true;

            }
            this.showValidationDialog = false;
            this.errorMessage = '';
            this.showErrorMessage = false;
            this.validationResult = {};
        },

        validateAndConfirmSavingChanges: function () {
            this.errorMessage = '';
            if (this.leaseInfo.registrationDate == "") {
                this.leaseInfo.registrationDate = null;
            }

            if (this.isCancelled) {
                this.saveChanges();
            }
            else {
                if (this.validateLeaseDates() == true) {
                    leaseService
                        .validateLeaseEdit(this.leaseInfo)
                        .then((res: any) => {
                            this.validationResult = res;
                            if (this.validationResult.errors.length > 0 || this.validationResult.warnings.length > 0) {
                                this.showValidationDialog = true;
                            }
                            else {
                                this.saveChanges();
                            }

                        })
                }
                else {
                    this.isEditMode = false;
                }
            }
        },
        saveChanges: function () {
            this.errorMessage = "";
            this.showValidationDialog = false;
            this.$store.dispatch("changeLoaderStatus", true);
            this.leaseInfo.isValid = !this.isCancelled;
            if (this.leaseInfo.registrationDate == "") {
                this.leaseInfo.registrationDate = null;
            }
            leaseService
                .editLeaseById(this.leaseInfo)
                .then((res) => {
                    if (res.success) {
                        if (res.id && res.id != this.$props.leaseId) {
                            this.$router.push({ path: `/admin/leases/${res.id}` }).catch(this.$handleNavigationError);
                        }
                        this.reloadData();
                        this.infoMessage = Messages.SaveSuccessful;
                        this.showInfoMessage = true;
                    }
                    else {
                        this.errorMessage = Messages.SaveError + " " + res.messages.join(', ')
                        this.showErrorMessage = true;
                    }
                })
                .catch((e) => {
                    this.errorMessage = Messages.SaveError;
                    this.showErrorMessage = true;   
                })
                .finally(() => this.$store.dispatch("changeLoaderStatus", false));
        },
        toggleEditMode: function () {
            if (this.isEditMode) {
                this.validateAndConfirmSavingChanges();
            } else {
                this.isEditMode = true;
            }
        },
        updateDates: function (val: any, key: string) {
            this.$data.leaseInfo[key] = val.date;
            this.$data.leaseInfo = Object.assign({}, this.$data.leaseInfo);
        },
        updateSelectedPeople: function (val: any) {
            this.errorMessage = "";
            //workaround for setting up the primary owner
            //and issues with reactivity in Vue https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
            if (val.newPeople && (val.newPeople.length === 1 || (val.newPeople.length > 0 && val.newPeople.findIndex((own: any) => own.isPrimary) == -1))) {
                val.newPeople[0].isPrimary = true;
            }
            if (!!this.leaseInfo.leaseOwners && val.newPeople && val.newPeople.length > 0) {
                this.leaseInfo.leaseOwners.splice(val.newPeople.length);
            }
            this.$set(this.leaseInfo, 'leaseOwners', val.newPeople);
        },
        getLeaseInfo() {
            leaseService
                .getLeaseById(this.leaseId)
                .then((res) => {
                    this.leaseInfo = res;
                    this.persistedDogId = res.dogId;
                    this.addNewMode = false;
                    this.isCancelled = !this.leaseInfo.isValid;
                });
        },
        validateLeaseDates() { 
            this.errorMessage =  "";
            this.showErrorMessage = false;
            let isValid = true;

            //do not validate lease information for leases that are cancelled.
            if (this.isCancelled) {
                return true;
            }

            if (this.leaseInfo.startDate == null || this.leaseInfo.endDate == null) {
                this.errorMessage = "Start Date and End Date are required.";
                this.showErrorMessage = true;
                return false;
            }
            if (this.leaseInfo.leaseOwners == null) {
                this.errorMessage = "At least one primary lease owner is required.";
                this.showErrorMessage = true;
                return false;
            }

            if (this.leaseInfo.startDate == "" || this.leaseInfo.endDate == "") {
                this.errorMessage = "Start Date and End Date are required.";
                this.showErrorMessage = true;
                return false;
            }

            return isValid;
        },
    },
    created: function () {
        this.reloadData();
    },
    watch: {
        leaseId: function (newVal: Number) {
            this.getLeaseInfo();
        },
    },
    props: {
        leaseId: { type: Number },
    },
    computed: {
        userInfo(): UserInfo {
            return this.$store.state.userInfo;
        },
        leaseOwnershipType: function () {
            return OwnershipType.Lease;
        },
        dogOwnershipType: function () {
            return OwnershipType.Dog;
        },
        
    },
});
</script>
